import { supabase } from '../supabaseClient';

// Get active notifications for current user
export const getActiveNotifications = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user found');

    const { data, error } = await supabase.rpc('get_active_notifications_for_user', {
      user_id_param: user.id
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching active notifications:', error);
    throw error;
  }
};

// Get all notifications (for admin)
export const getAllNotifications = async () => {
  try {
    const { data, error } = await supabase
      .from('customer_notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching all notifications:', error);
    throw error;
  }
};

// Get notification by ID
export const getNotificationById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('customer_notifications')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching notification:', error);
    throw error;
  }
};

// Create notification
export const createNotification = async (notification) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user found');

    const { data, error } = await supabase
      .from('customer_notifications')
      .insert({
        ...notification,
        created_by: user.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Update notification
export const updateNotification = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('customer_notifications')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating notification:', error);
    throw error;
  }
};

// Delete notification
export const deleteNotification = async (id) => {
  try {
    const { error } = await supabase
      .from('customer_notifications')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

// Dismiss notification for current user
export const dismissNotification = async (notificationId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user found');

    const { error } = await supabase
      .from('notification_dismissals')
      .insert({
        notification_id: notificationId,
        user_id: user.id
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error dismissing notification:', error);
    throw error;
  }
};

// Get company list for targeting
export const getCompanyList = async () => {
  try {
    const { data, error } = await supabase.rpc('get_company_list');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching company list:', error);
    throw error;
  }
};

// Get notification statistics
export const getNotificationStats = async (notificationId) => {
  try {
    const { data, error } = await supabase.rpc('get_notification_stats', {
      notification_id_param: notificationId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching notification stats:', error);
    throw error;
  }
};

// Send notification emails
export const sendNotificationEmails = async (notificationId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No session found');

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-customer-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ notificationId }),
      }
    );

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send emails');
    }

    return result;
  } catch (error) {
    console.error('Error sending notification emails:', error);
    throw error;
  }
};

// Get email logs for a notification
export const getEmailLogs = async (notificationId) => {
  try {
    const { data, error } = await supabase
      .from('notification_email_log')
      .select('*')
      .eq('notification_id', notificationId)
      .order('sent_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching email logs:', error);
    throw error;
  }
};

// Calculate estimated recipients
export const calculateRecipients = async (targetType, targetCompanies) => {
  try {
    let query = supabase
      .from('accounts')
      .select('id', { count: 'exact', head: true })
      .not('email', 'is', null);

    if (targetType === 'specific_companies' && targetCompanies && targetCompanies.length > 0) {
      query = query.in('company_name', targetCompanies);
    }

    const { count, error } = await query;

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error calculating recipients:', error);
    throw error;
  }
};
